/// <amd-module name="Artifacts/Root/services/FeedResolvingService" />

import * as Constants from "Artifacts/Root/Constants";
import { FeedServiceInstanceId, RegistryConstants } from "Artifacts/Root/Constants";
import { loadData } from "Artifacts/Root/helpers/DataProviderHelpers";
import { IFeed } from "Artifacts/Root/types";
import { ArtifactsFeed } from "Artifacts/Root/types/AzureArtifacts";
import { Feed, FeedView } from "Artifacts/Root/web-apis/contracts/Feed";
import { getFeedClient, IFeedRestClient } from "Artifacts/Root/web-apis/RestClient/Feed";
import { IVssRestClientOptions, IVssService, Services, VssService } from "VSS/Platform/Context";
import { ISettingsRestClient } from "VSS/Platform/RestClient/Settings";

export interface IFeedResolvingService extends IVssService {
  getFeed(feedIdentifier: string, allowRedirect: boolean): Promise<IFeed | null>;
  getFeeds(): Promise<Feed[]>;
  getFeedViews(feed: Feed): Promise<FeedView[]>;
  setMruFullyQualifiedFeedId(fullyQualifiedFeedId: string): Promise<void>;
  getBadgePackageProtocol(
    feedIdentifier: string,
    projectIdentifier: string | undefined,
    packageIdentifier: string,
  ): Promise<string>;
}

export class FeedResolvingService extends VssService implements IFeedResolvingService {
  public async getFeed(feedIdentifier: string, allowRedirect: boolean): Promise<IFeed> {
    const data = await loadData<ArtifactsFeed>(
      this.pageContext,
      "ms.azure-artifacts.feed-resolving-data-provider",
      { feedIdentifier: feedIdentifier, allowRedirect: allowRedirect },
      false,
      Constants.CommonConstants.FeedServiceInstanceId,
    );

    // We can safely typecast because IFeed matches the shape of the data returned by the data provider
    return data as IFeed;
  }

  public async getFeeds(): Promise<Feed[]> {
    const feedRestClient: IFeedRestClient = getFeedClient(this.pageContext);
    // TODO: forward arguments to getFeeds.
    return feedRestClient.getFeeds(undefined, undefined, false, true);
  }

  public async getFeedViews(feed: Feed): Promise<FeedView[]> {
    const feedRestClient: IFeedRestClient = getFeedClient(this.pageContext);
    return feedRestClient.getFeedViews(feed.id, feed.project?.id);
  }

  public setMruFullyQualifiedFeedId(feedId: string): Promise<void> {
    const settingsClient = this.pageContext.getRestClient<ISettingsRestClient>(
      "ISettingsRestClient",
      {
        serviceInstanceType: FeedServiceInstanceId,
      } as IVssRestClientOptions,
    );
    const entries: { [key: string]: any } = {};
    entries[RegistryConstants.MruFeedIdPath] = feedId;

    return settingsClient.setEntries(entries, "me");
  }

  public async getBadgePackageProtocol(
    feedId: string,
    projectId: string | undefined,
    packageId: string,
  ): Promise<string> {
    const feedRestClient: IFeedRestClient = getFeedClient(this.pageContext);
    const data = await feedRestClient.getPackage(feedId, packageId, projectId);

    return data.protocolType;
  }
}

Services.add("IFeedResolvingService", { serviceFactory: FeedResolvingService });
